import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import createActivityDetector from "activity-detector";
import { FunctionComponent, useState } from "react";
import CookieConsent from "react-cookie-consent";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import Menu from "../components/Menu";
import MobileBottomNav from "../components/MobileBottomNav";
import TeacherActiveAppBar from "../components/TeacherActiveAppBar";

interface Props {}

const Layout: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { isLoading, isAuthenticated, logout } = useAuth0();
  const [appBarHeight, setAppBarHeight] = useState<number>(0);
  const [bottomNavHeight, setBottomNavHeight] = useState<number>(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const paddingTop = `calc(${appBarHeight}px + 15px)`;
  const paddingBottom = `calc(${bottomNavHeight}px + 15px)`;

  const { t } = useTranslation();

  window.addEventListener(
    "blur",
    (event) => {
      event.stopImmediatePropagation();
    },
    true
  );

  const activityDetector = createActivityDetector({
    timeToIdle: 300000,
    activityEvents: [
      "click",
      "mousemove",
      "keydown",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "focus",
    ],
    inactivityEvents: [],
  });

  activityDetector.on("idle", () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  });

  return (
    <>
      {isAuthenticated && (
        <>
          <TeacherActiveAppBar setAppBarHeight={setAppBarHeight} />

          <Container
            fixed
            sx={{
              backgroundColor: "#ffffff",
              minHeight: isSmallScreen
                ? "100vh"
                : `calc(100vh - ${appBarHeight}px - 15px)`,
              height: isSmallScreen ? "unset" : "unset",
              marginTop: isSmallScreen ? 0 : "15px",
              marginBottom: isSmallScreen ? 0 : "15px",
              paddingTop: isSmallScreen ? paddingTop : "15px",
              paddingBottom: isSmallScreen ? paddingBottom : "15px",
              borderRadius: isSmallScreen ? "0" : "10px 10px 0px 0px",
              width: isSmallScreen ? "calc(100vw - 30px)" : "fixed",
            }}
            disableGutters
          >
            {isLoading && (
              <Box
                paddingTop={4}
                display="flex"
                justifyContent="center"
                sx={{
                  minHeight: isSmallScreen
                    ? `calc(100vh - ${appBarHeight}px - ${bottomNavHeight}px -30px)`
                    : `calc(100vh - ${appBarHeight}px - 15px)`,
                }}
              >
                <CircularProgress size={"50px"} />
              </Box>
            )}
            {!isLoading && isAuthenticated && (
              <Grid
                container
                item
                sx={{
                  minHeight: isSmallScreen
                    ? `calc(100vh - ${appBarHeight}px - ${bottomNavHeight}px -30px)`
                    : `calc(100vh - ${appBarHeight}px - 15px)`,
                }}
              >
                {!isSmallScreen && (
                  <Grid
                    padding={2}
                    item
                    xs={isMediumScreen ? 3 : 2.5}
                    sx={{
                      borderRight: "1px solid #dcdcdc",
                      minHeight: `calc(100vh - ${appBarHeight}px - 15px)`,
                    }}
                  >
                    <Menu />
                  </Grid>
                )}
                <Grid
                  item
                  xs={isMediumScreen ? 9 : !isSmallScreen ? 9.5 : 12}
                  padding={2}
                  sx={{
                    minHeight: isSmallScreen
                      ? `calc(100vh - ${appBarHeight}px - ${bottomNavHeight}px -30px)`
                      : `calc(100vh - ${appBarHeight}px - 15px)`,
                    // backgroundColor: "pink"
                  }}
                >
                  <Outlet />
                </Grid>
              </Grid>
            )}
            {!isLoading && !isAuthenticated && <Outlet />}
          </Container>

          {isSmallScreen && (
            <MobileBottomNav setBottomNavHeight={setBottomNavHeight} />
          )}

          <CookieConsent
            enableDeclineButton
            flipButtons
            location="none"
            buttonText={t("cookieConsent.accept")}
            declineButtonText={t("cookieConsent.decline")}
            cookieName="teacheractive-timesheets"
            expires={150}
            buttonStyle={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: theme.shape.borderRadius,
              color: "#ffffff",
              fontWeight: "bold",
              boxShadow: theme.shadows[2],
              padding: "10px",
            }}
            declineButtonStyle={{
              backgroundColor: "transparent",
              color: "black",
              fontSize: "14px",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
            style={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              padding: "10px",
              position: "fixed",
              bottom: isSmallScreen ? `${bottomNavHeight}px` : "0",
              width: "100%",
              zIndex: 1000,
            }}
          >
            <Typography variant="body1">{t("cookieConsent.text")}</Typography>
          </CookieConsent>
        </>
      )}
    </>
  );
};

export default Layout;
